import React from 'react'
import { Table as RsuiteTable } from 'rsuite'
import { RowDataType } from 'rsuite-table'
import { HeaderCell, Cell, ActionCell } from './cells'

const { Column } = RsuiteTable

/**
 * Equal-width defaults applied to every configured column. Each column gets the
 * same `flexGrow`, so the table divides the available width into identical
 * columns. A column can opt out by setting an explicit `width`.
 */
export const DEFAULT_COLUMN_FLEX_GROW = 1
/** Floor width: below this the table scrolls horizontally instead of squashing. */
export const DEFAULT_COLUMN_MIN_WIDTH = 140

export interface TableActionConfig<R = any> {
  /** Fired when an action icon (or the toggle) is clicked. */
  onAction: (eventKey: string | undefined, rowData: R) => void;
  /** Visible action icons — array or per-row resolver. Include 'Toggle' for the switch. */
  actionOptions?: string[] | ((rowData: R) => string[]);
  /** Actions to render disabled — array or per-row resolver. */
  disabledActions?: string[] | ((rowData: R) => string[]);
  /** Row flag that drives the toggle's checked state (defaults to `isActive`). */
  toggleKey?: string;
  /** Row key on which a `false` value hides the Delete action. */
  isDeleteHideOn?: string;
  /** Row key passed through to the cell (defaults to `id`). */
  dataKey?: string;
}

export interface TableColumnConfig<R = any> {
  /** Stable identity for the column (also used as the React key). */
  key: string;
  /** Header text. */
  label: React.ReactNode;
  /** Row field to display when no `render` is supplied. */
  dataKey?: string;
  /** Custom cell renderer; takes precedence over `dataKey`. */
  render?: (rowData: R) => React.ReactNode;
  /** Cell text alignment. */
  align?: 'left' | 'center' | 'right';
  /** Opt out of equal-width sizing with a fixed pixel width. */
  width?: number;
  /** Override the equal-width flex ratio (e.g. 2 for a column twice as wide). */
  flexGrow?: number;
  /** Override the default minimum width. */
  minWidth?: number;
  /** Pin the column while the table scrolls horizontally. */
  fixed?: boolean | 'left' | 'right';
  /** Inline style for the cell. */
  cellStyle?: React.CSSProperties;
  /** Tooltip text/resolver for the cell. */
  tooltip?: string | ((rowData: R) => string);
  /** Marks this as the action column; renders an `ActionCell` instead of a value cell. */
  action?: TableActionConfig<R>;
}

/**
 * Turn a column config array into rsuite `<Column>` elements. Every column is
 * equal-width by default (`flexGrow: 1` plus a shared `minWidth`); set `width`
 * on an individual column to give it a fixed size instead.
 */
export function renderTableColumns<R extends RowDataType<any>>(
  columns: TableColumnConfig<R>[]
): JSX.Element[] {
  return columns.map((col) => {
    const hasFixedWidth = typeof col.width === 'number'
    const sizing = hasFixedWidth
      ? { width: col.width }
      : {
          flexGrow: col.flexGrow ?? DEFAULT_COLUMN_FLEX_GROW,
          minWidth: col.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH
        }

    let cell: JSX.Element
    if (col.action) {
      cell = (
        <ActionCell
          dataKey={col.action.dataKey ?? 'id'}
          onAction={col.action.onAction}
          actionOptions={col.action.actionOptions}
          disabledActions={col.action.disabledActions}
          toggleKey={col.action.toggleKey}
          isDeleteHideOn={col.action.isDeleteHideOn}
        />
      )
    } else if (col.render) {
      cell = (
        <Cell style={col.cellStyle} tooltip={col.tooltip}>
          {(rowData: any) => col.render!(rowData)}
        </Cell>
      )
    } else {
      cell = (
        <Cell dataKey={col.dataKey} style={col.cellStyle} tooltip={col.tooltip} />
      )
    }

    return (
      <Column key={col.key} align={col.align} fixed={col.fixed} {...sizing}>
        <HeaderCell>{col.label}</HeaderCell>
        {cell}
      </Column>
    )
  })
}
